import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import '../vulnerabilities.css';
import '../../assets/assets.css';
import {
  getDashboardVulnerabilities,
  patchDashboardVulnerabilityStatus,
  postDashboardVulnerabilityNote,
  deleteDashboardVulnerability,
} from '../../../services/risk.service';
import { REPORT_TYPE_OPTIONS, generateAIReport } from '../../../services/report.service';
import {
  compareLocaleStrings,
  stableSortTableRows,
  toggleTableSortConfig,
  getTableSortIcon,
} from '../../../utils/tableSort';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  riskDetailPath,
  buildRisksPathWithFilters,
  parseRisksFiltersFromSearchParams,
} from '../../../routes/dashboardRoutes';
import BlueXLoader, { PageDataLoader } from '../../../components/BlueXLoader';
import { queryClient } from '../../../queryClient';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';
import { findingDetectedMs, overviewDaysParamToNumber } from '../../dashboard-home/overviewDateFilter';
import InfoTooltip from '../../dashboard-home/components/InfoTooltip';

const RISKS_CACHE_MS = 3 * 60 * 1000;
const RISK_RANGE_LABELS = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '6m': 'Last 6 months',
  '1y': 'Last year',
};

function risksListQueryKey(orgQuery, statusFilter, severityFilter, scannerFilter, dateFilter, rangeFilter) {
  return [
    'risks-page-v3',
    String(orgQuery?.org_id || 'all'),
    String(orgQuery?.site_uuid || orgQuery?.site_id || 'all'),
    statusFilter || 'all',
    severityFilter || '',
    scannerFilter || '',
    dateFilter || '',
    rangeFilter || '',
  ];
}

function rowsFromRisksCache(cached) {
  if (Array.isArray(cached)) return cached;
  if (Array.isArray(cached?.rows)) return cached.rows;
  return null;
}

function filterNormalizedRisksByRange(rows, rangeToken) {
  const days = overviewDaysParamToNumber(rangeToken);
  if (days == null) return Array.isArray(rows) ? rows : [];
  const cutoffMs = Date.now() - days * 86400000;
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const t = findingDetectedMs(row?.raw || { date_time: row?.detectedAt });
    if (Number.isNaN(t)) return false;
    return t >= cutoffMs;
  });
}

function formatRiskRangeFilterLabel(rangeToken) {
  const key = String(rangeToken || '').trim().toLowerCase();
  return RISK_RANGE_LABELS[key] || key;
}

function riskDetectedDayKey(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const isoPrefix = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) return isoPrefix[1];
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRiskDateFilterLabel(dateIso) {
  const key = riskDetectedDayKey(dateIso);
  if (!key) return '';
  const ms = Date.parse(`${key}T00:00:00`);
  if (Number.isNaN(ms)) return key;
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const RISK_SEVERITY_ORDER = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const RISK_NOTES_OVERRIDES_KEY = 'xspectre_risk_notes_overrides';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const RISKS_TABLE_COLUMNS = [
  { key: 'checkbox', label: 'Select', sortKey: null, resizable: false, defaultWidth: 44, minWidth: 44 },
  { key: 'severity', label: 'Threat', sortKey: 'severity', resizable: true, defaultWidth: 120, minWidth: 100 },
  { key: 'target', label: 'IP Address', sortKey: 'target', resizable: true, defaultWidth: 160, minWidth: 100 },
  { key: 'port', label: 'Port', sortKey: 'port', resizable: true, defaultWidth: 96, minWidth: 80 },
  { key: 'cvss', label: 'CVSS', sortKey: 'cvss', resizable: true, defaultWidth: 72, minWidth: 64 },
  { key: 'title', label: 'Vulnerability', sortKey: 'title', resizable: true, defaultWidth: 248, minWidth: 140 },
  { key: 'scanner', label: 'Scanner', sortKey: 'scanner', resizable: true, defaultWidth: 100, minWidth: 88 },
  { key: 'status', label: 'Status', sortKey: 'status', resizable: false, defaultWidth: 156, minWidth: 156, maxWidth: 156 },
];

const RISKS_TABLE_COLUMN_WIDTHS_KEY = 'xspectre_risks_table_column_widths_v16';

function normalizeRiskSeverity(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'critical') return 'Critical';
  if (raw === 'high') return 'High';
  if (raw === 'medium') return 'Medium';
  if (raw === 'low') return 'Low';
  if (raw === 'log' || raw === 'info' || raw === 'informational') return 'Low';
  return 'Unknown';
}

function normalizeRiskState(raw) {
  if (String(raw?.accepted_vuln || '').trim().toLowerCase() === 'yes') return 'accepted';
  if (String(raw?.false_positive || '').trim().toLowerCase() === 'yes') return 'false_positive';
  const status = String(raw?.vuln_status || '').trim().toLowerCase();
  if (status === 'duplicate') return 'duplicate';
  if (status === 'closed') return 'closed';
  return 'open';
}

function getCvssSeverityBand(scoreValue) {
  const numericScore = Number.parseFloat(scoreValue);
  if (!Number.isFinite(numericScore)) return 'unknown';
  if (numericScore >= 9.0) return 'critical';
  if (numericScore >= 7.0) return 'high';
  if (numericScore >= 4.0) return 'medium';
  if (numericScore >= 0.0) return 'low';
  return 'unknown';
}

function isRiskTargetPlaceholder(value) {
  if (value == null) return true;
  const text = String(value).trim();
  if (!text) return true;
  const normalized = text.toLowerCase();
  return (
    normalized === 'na' ||
    normalized === 'n/a' ||
    normalized === 'n\\a' ||
    normalized === 'none' ||
    normalized === 'null' ||
    normalized === 'unknown' ||
    normalized === '-' ||
    normalized === '--'
  );
}

function pickRiskTarget(...values) {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (isRiskTargetPlaceholder(value)) continue;
    return String(value).trim();
  }
  return '';
}

function formatRiskCvssScore(value) {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toFixed(1);
}

function formatRiskScannerLabel(scannerType, deploymentHint = '') {
  const type = String(scannerType || '').trim().toLowerCase();
  if (type !== 'network') return 'Web Scan';
  const deployment = String(deploymentHint || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
  if (
    deployment === 'perimeter_scanner'
    || deployment === 'perimeter'
    || deployment.startsWith('perimeter')
  ) {
    return 'Perimeter';
  }
  if (
    deployment === 'on_premise'
    || deployment === 'onprem'
    || deployment === 'on_prem'
    || deployment.startsWith('on_prem')
  ) {
    return 'On-premise';
  }
  return 'On-premise';
}

function inferSourceKeysFromRiskRow(row) {
  const scannerType = String(row?.scannerType || row?.raw?.scanner_type || '').trim().toLowerCase();
  if (scannerType === 'network') return ['openvas'];
  if (scannerType === 'web') return ['zap'];
  return [];
}

function normalizeRiskRow(raw, scannerTypeHint = '') {
  const scannerType = String(raw?.scanner_type || scannerTypeHint || '').trim().toLowerCase() === 'network' ? 'network' : 'web';
  const severity = normalizeRiskSeverity(raw?.severity);
  const ip = pickRiskTarget(raw?.ip, raw?.ip_address);
  const hostname = pickRiskTarget(raw?.hostname, raw?.host);
  // Network host badges on the dashboard navigate with IP in `q` — prefer IP as the
  // display/search target so click-through matches Top Vulnerable Hosts.
  const target = scannerType === 'web'
    ? pickRiskTarget(
      raw?.url,
      raw?.target,
      hostname,
      raw?.endpoint,
      ip,
      raw?.scan_definition
    )
    : pickRiskTarget(
      ip,
      hostname,
      raw?.target,
      raw?.scan_definition,
      raw?.endpoint,
      raw?.url
    );
  const port = String(raw?.port || '').trim();
  const status = normalizeRiskState(raw);
  const scannerDeployment = String(
    raw?.scanner_deployment || raw?.scannerDeployment || raw?.deployment_type || ''
  ).trim();
  return {
    id: raw?.vuln_id || `${scannerType}:${raw?.title || ''}:${target}:${port}`,
    title: String(raw?.title || '').trim() || 'Untitled vulnerability',
    source: formatRiskScannerLabel(scannerType, scannerDeployment),
    scannerType,
    scannerDeployment,
    severity,
    severityKey: severity.toLowerCase(),
    target,
    ip,
    hostname,
    port,
    detectedAt: raw?.date_time || raw?.created_at || raw?.updated_at || '',
    status,
    statusLabel: status === 'false_positive' ? 'False Positive' : status.charAt(0).toUpperCase() + status.slice(1),
    cvssScore: raw?.cvss_score ?? raw?.severity_float ?? raw?.cvss ?? null,
    raw,
  };
}

function riskStatusLabelFromValue(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  if (status === 'false_positive') return 'False Positive';
  if (status === 'accepted') return 'Accepted';
  if (status === 'duplicate') return 'Duplicate';
  if (status === 'closed') return 'Closed';
  return 'Open';
}

const RISK_STATUS_ACTION_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'false_positive', label: 'False Positive' },
  { value: 'closed', label: 'Closed' },
];

function loadLocalRiskNotes(riskId) {
  try {
    const raw = window.localStorage.getItem(RISK_NOTES_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const notes = parsed?.[riskId];
    return Array.isArray(notes) ? notes : [];
  } catch (_) {
    return [];
  }
}

function persistLocalRiskNotes(riskId, notes) {
  try {
    const raw = window.localStorage.getItem(RISK_NOTES_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === 'object' ? parsed : {};
    next[riskId] = notes;
    window.localStorage.setItem(RISK_NOTES_OVERRIDES_KEY, JSON.stringify(next));
  } catch (_) {
    /* ignore storage errors */
  }
}

function RiskStatusSelect({ rowId, value, label, ariaLabel, open, onOpenChange, onChange }) {
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const status = String(value || 'open').trim().toLowerCase() || 'open';
  const selectedLabel = label || riskStatusLabelFromValue(status);

  const syncMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const gap = 6;
    const menuWidth = Math.max(rect.width, 126);
    let left = rect.left;
    if (left + menuWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - menuWidth - 8);
    }
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + gap,
      left,
      width: menuWidth,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    syncMenuPosition();
    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    return () => {
      window.removeEventListener('resize', syncMenuPosition);
      window.removeEventListener('scroll', syncMenuPosition, true);
    };
  }, [open, syncMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inRoot && !inMenu) onOpenChange(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div
      className={`risks-list-status-select${open ? ' is-open' : ''}`}
      ref={rootRef}
    >
      <button
        id={`risk-action-${rowId}`}
        ref={buttonRef}
        type="button"
        className={`risks-list-action-select risks-list-action-select--${status}${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || `Status: ${selectedLabel}`}
        onClick={(event) => {
          event.stopPropagation();
          onOpenChange(!open);
        }}
      >
        <span className="risks-list-action-select-label">{selectedLabel}</span>
        <span className="risks-list-action-select-chevron" aria-hidden="true" />
      </button>
      {open && menuStyle
        ? createPortal(
            <ul
              ref={menuRef}
              className={`risks-list-status-menu${
                document.querySelector('.dashboard-container.theme-light')
                  ? ' risks-list-status-menu--light'
                  : ''
              }`}
              role="listbox"
              aria-label="Select status"
              style={menuStyle}
            >
              {RISK_STATUS_ACTION_OPTIONS.map((option) => (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={status === option.value}
                    className={`risks-list-status-menu-option risks-list-status-menu-option--${option.value}${
                      status === option.value ? ' is-selected' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(option.value);
                      onOpenChange(false);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
}

function resolveRiskDetailStatusValue(raw, row) {
  if (raw && typeof raw === 'object' && Object.keys(raw).length > 0) {
    return normalizeRiskState(raw);
  }
  const rowStatus = String(row?.status || 'open').trim().toLowerCase();
  return rowStatus || 'open';
}

function scanVulnToRiskRow(vuln, scanType, targetLabel, detail) {
  const target = String(
    targetLabel || detail?.target || vuln?.url || vuln?.hostname || vuln?.target || ''
  ).trim();
  const scannerType = String(scanType || '').trim().toLowerCase() === 'network' ? 'network' : 'web';
  return normalizeRiskRow(
    {
      ...vuln,
      target,
      url: scannerType === 'web' ? target : vuln?.url,
      hostname: scannerType === 'network' ? target : vuln?.hostname,
      scanner_type: scannerType,
    },
    scannerType
  );
}

const RisksPageContent = ({ filterSeed, universalSearchQuery = '', orgQuery = null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlFilters = useMemo(
    () => parseRisksFiltersFromSearchParams(searchParams),
    [searchParams]
  );
  const RISK_STATUS_OVERRIDES_KEY = 'xspectre_risk_status_overrides';
  const initialRisksKey = risksListQueryKey(
    orgQuery,
    urlFilters.status || filterSeed?.status || '',
    urlFilters.severity || filterSeed?.severity || '',
    urlFilters.scanner || filterSeed?.scanner || '',
    urlFilters.date || filterSeed?.date || '',
    urlFilters.range || filterSeed?.range || ''
  );
  const [rows, setRows] = useState(() => rowsFromRisksCache(queryClient.getQueryData(initialRisksKey)) || []);
  const [loading, setLoading] = useState(() => rowsFromRisksCache(queryClient.getQueryData(initialRisksKey)) == null);
  const [error, setError] = useState('');
  const [severityFilter, setSeverityFilter] = useState(urlFilters.severity || filterSeed?.severity || '');
  const [scannerFilter, setScannerFilter] = useState(urlFilters.scanner || filterSeed?.scanner || '');
  const [statusFilter, setStatusFilter] = useState(urlFilters.status || filterSeed?.status || '');
  const [dateFilter, setDateFilter] = useState(
    () => String(urlFilters.date || filterSeed?.date || '').trim()
  );
  const [rangeFilter, setRangeFilter] = useState(
    () => String(urlFilters.range || filterSeed?.range || '').trim().toLowerCase()
  );
  const [dateFilterRelaxed, setDateFilterRelaxed] = useState(false);
  const risksQueryKey = useMemo(
    () => risksListQueryKey(orgQuery, statusFilter, severityFilter, scannerFilter, dateFilter, rangeFilter),
    [orgQuery, statusFilter, severityFilter, scannerFilter, dateFilter, rangeFilter]
  );
  const risksQueryKeyRef = useRef(risksQueryKey);
  risksQueryKeyRef.current = risksQueryKey;
  const rowsRef = useRef(rows);
  const risksDataRevision = useRef(0);
  rowsRef.current = rows;
  const [riskStatusOverrides, setRiskStatusOverrides] = useState(() => {
    try {
      const raw = window.localStorage.getItem(RISK_STATUS_OVERRIDES_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  });
  const [severityOpen, setSeverityOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState(
    () => String(urlFilters.q || filterSeed?.q || '').trim()
  );
  const [statusNoteConfirm, setStatusNoteConfirm] = useState(null);
  const [statusNoteValue, setStatusNoteValue] = useState('');
  const [statusNoteSaving, setStatusNoteSaving] = useState(false);
  const [statusNoteError, setStatusNoteError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const severityRef = useRef(null);
  const scannerRef = useRef(null);
  const statusRef = useRef(null);
  const bulkStatusRef = useRef(null);
  const selectAllRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const tourRiskRowsRef = useRef([]);
  const tableSearchId = useId();
  const statusNoteDescId = useId();
  const statusNoteInputId = useId();
  const deleteConfirmDescId = useId();
  const [sortConfig, setSortConfig] = useState({ key: 'severity', direction: 'desc' });
  const [selectedRiskIds, setSelectedRiskIds] = useState([]);
  const [openRiskStatusMenuId, setOpenRiskStatusMenuId] = useState(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);
  const [reportTemplateKey, setReportTemplateKey] = useState(REPORT_TYPE_OPTIONS[0].template_key);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportGenerationBusy, setReportGenerationBusy] = useState(false);
  const [reportGenerationError, setReportGenerationError] = useState('');
  const [headerActionsHost, setHeaderActionsHost] = useState(null);

  useLayoutEffect(() => {
    setHeaderActionsHost(document.getElementById('risks-page-header-actions'));
  }, []);

  const handleRiskSort = useCallback((key) => {
    setSortConfig((prev) => toggleTableSortConfig(prev, key));
  }, []);

  const getRiskSortIcon = useCallback((key) => getTableSortIcon(sortConfig, key), [sortConfig]);

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
    setOpenRiskStatusMenuId(null);
  }, []);

  const { widths: riskColumnWidths, onResizeStart: onRiskColumnResizeStart, tableMinWidth: risksTableMinWidth, resizingKey: riskColumnResizingKey } =
    useTableColumnResize(RISKS_TABLE_COLUMNS, RISKS_TABLE_COLUMN_WIDTHS_KEY);

  const severityOptions = useMemo(
    () => [
      { value: '', label: 'All Threats' },
      { value: 'Critical', label: 'Critical' },
      { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' },
      { value: 'Low', label: 'Low' },
    ],
    []
  );
  const scannerOptions = useMemo(
    () => [
      { value: '', label: 'All Scanner Types' },
      { value: 'web', label: 'Web Scan' },
      { value: 'network', label: 'Network' },
    ],
    []
  );
  const statusOptions = useMemo(
    () => [
      { value: '', label: 'All Statuses' },
      { value: 'open', label: 'Open' },
      { value: 'accepted', label: 'Accepted' },
      { value: 'closed', label: 'Closed' },
      { value: 'false_positive', label: 'False Positive' },
    ],
    []
  );

  const selectedSeverityLabel = severityOptions.find((opt) => opt.value === severityFilter)?.label || 'All Threats';
  const selectedScannerLabel = scannerOptions.find((opt) => opt.value === scannerFilter)?.label || 'All Scanner Types';
  const selectedStatusLabel = statusOptions.find((opt) => opt.value === statusFilter)?.label || 'All Statuses';
  const isRiskFilterPanelOpen = severityOpen || scannerOpen || statusOpen || bulkStatusOpen;
  const selectedCount = selectedRiskIds.length;

  const closeRiskFilterPanels = useCallback(() => {
    setSeverityOpen(false);
    setScannerOpen(false);
    setStatusOpen(false);
    setBulkStatusOpen(false);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(RISK_STATUS_OVERRIDES_KEY, JSON.stringify(riskStatusOverrides || {}));
    } catch (_) {
      /* ignore storage errors */
    }
  }, [riskStatusOverrides]);

  useEffect(() => {
    setSeverityFilter(urlFilters.severity || '');
    setScannerFilter(urlFilters.scanner || '');
    setStatusFilter(urlFilters.status || '');
    setTableSearch(String(urlFilters.q || '').trim());
    setDateFilter(String(urlFilters.date || '').trim());
    setRangeFilter(String(urlFilters.range || '').trim().toLowerCase());
  }, [urlFilters.severity, urlFilters.scanner, urlFilters.status, urlFilters.q, urlFilters.date, urlFilters.range, searchParams]);

  useEffect(() => {
    let cancelled = false;
    const cached = queryClient.getQueryData(risksQueryKey);
    const cachedRows = rowsFromRisksCache(cached);
    if (cachedRows) {
      setRows(cachedRows);
      setDateFilterRelaxed(Boolean(cached?.dateRelaxed));
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError('');
    setDateFilterRelaxed(false);
    const revisionAtStart = risksDataRevision.current;

    const normalizeList = (data) =>
      [
        ...(Array.isArray(data?.web) ? data.web.map((row) => normalizeRiskRow(row, 'web')) : []),
        ...(Array.isArray(data?.network) ? data.network.map((row) => normalizeRiskRow(row, 'network')) : []),
      ].sort((a, b) => {
        const severityDiff = (RISK_SEVERITY_ORDER[b.severity] || 0) - (RISK_SEVERITY_ORDER[a.severity] || 0);
        if (severityDiff !== 0) return severityDiff;
        const aTime = a.detectedAt ? new Date(a.detectedAt).getTime() : 0;
        const bTime = b.detectedAt ? new Date(b.detectedAt).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;
        return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      });

    queryClient.fetchQuery({
      queryKey: risksQueryKey,
      staleTime: RISKS_CACHE_MS,
      queryFn: async () => {
        const baseQuery = {
          ...(orgQuery || {}),
          status: statusFilter || 'all',
          severity: severityFilter || undefined,
          scanner: scannerFilter || undefined,
        };
        // Single calendar day (trend chart) or rolling overview range (Top Hosts).
        if (dateFilter) {
          const dated = normalizeList(
            await getDashboardVulnerabilities({ ...baseQuery, date: dateFilter })
          );
          const matched = dated.filter((row) => riskDetectedDayKey(row.detectedAt) === dateFilter);
          if (matched.length > 0) {
            return { rows: matched, dateRelaxed: false };
          }
          // Portal may ignore `date` and omit date_time — keep severity results instead of an empty table.
          if (dated.length > 0) {
            return { rows: dated, dateRelaxed: true };
          }
          const unscoped = normalizeList(await getDashboardVulnerabilities(baseQuery));
          const unscopedMatched = unscoped.filter(
            (row) => riskDetectedDayKey(row.detectedAt) === dateFilter
          );
          if (unscopedMatched.length > 0) {
            return { rows: unscopedMatched, dateRelaxed: false };
          }
          return { rows: unscoped, dateRelaxed: unscoped.length > 0 };
        }
        if (rangeFilter) {
          // Trust the API range payload as-is. Do not client-filter it down —
          // portal rows often omit date_time, which would hide valid findings
          // (e.g. 14 API rows → 11 after a browser-side date window).
          const ranged = normalizeList(
            await getDashboardVulnerabilities({ ...baseQuery, range: rangeFilter })
          );
          if (ranged.length > 0) {
            return { rows: ranged, dateRelaxed: false };
          }
          const unscoped = normalizeList(await getDashboardVulnerabilities(baseQuery));
          const unscopedMatched = filterNormalizedRisksByRange(unscoped, rangeFilter);
          if (unscopedMatched.length > 0) {
            return { rows: unscopedMatched, dateRelaxed: false };
          }
          return { rows: unscoped, dateRelaxed: unscoped.length > 0 };
        }
        return {
          rows: normalizeList(await getDashboardVulnerabilities(baseQuery)),
          dateRelaxed: false,
        };
      },
    })
      .then((payload) => {
        if (cancelled) return;
        if (risksDataRevision.current !== revisionAtStart) {
          queryClient.setQueryData(risksQueryKey, {
            rows: rowsRef.current,
            dateRelaxed: false,
          });
          setLoading(false);
          return;
        }
        const nextRows = Array.isArray(payload?.rows) ? payload.rows : Array.isArray(payload) ? payload : [];
        setRows(nextRows);
        setDateFilterRelaxed(Boolean(payload?.dateRelaxed));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!cachedRows) setRows([]);
        setDateFilterRelaxed(false);
        setError(err?.message || 'Unable to load risks');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateFilter, rangeFilter, orgQuery, risksQueryKey, scannerFilter, severityFilter, statusFilter]);

  const rowsWithStatusOverrides = useMemo(
    () =>
      rows.map((row) => {
        const override = riskStatusOverrides?.[row.id];
        if (!override) return row;
        return {
          ...row,
          status: override,
          statusLabel: riskStatusLabelFromValue(override),
        };
      }),
    [rows, riskStatusOverrides]
  );

  useEffect(() => {
    if (!isRiskFilterPanelOpen) return undefined;
    const onDoc = (event) => {
      if (severityRef.current && !severityRef.current.contains(event.target)) setSeverityOpen(false);
      if (scannerRef.current && !scannerRef.current.contains(event.target)) setScannerOpen(false);
      if (statusRef.current && !statusRef.current.contains(event.target)) setStatusOpen(false);
      if (bulkStatusRef.current && !bulkStatusRef.current.contains(event.target)) setBulkStatusOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') closeRiskFilterPanels();
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [closeRiskFilterPanels, isRiskFilterPanelOpen]);

  const filteredRows = useMemo(() => {
    // Date scoping is applied in the fetch path (API / match / relax). Do not
    // re-filter here — portal list payloads often omit date_time, which would
    // wipe the table to empty.
    const statusScopedRows =
      statusFilter && statusFilter !== 'all'
        ? rowsWithStatusOverrides.filter((row) => row.status === statusFilter)
        : rowsWithStatusOverrides;
    const query = String(tableSearch || universalSearchQuery || '').trim().toLowerCase();
    if (!query) return statusScopedRows;
    return statusScopedRows.filter((row) =>
      [
        row.title,
        row.source,
        row.target,
        row.ip,
        row.hostname,
        row.port,
        row.severity,
        row.statusLabel,
        formatRiskCvssScore(row.cvssScore),
        row.raw?.ip,
        row.raw?.ip_address,
        row.raw?.hostname,
        row.raw?.host,
        row.raw?.url,
        row.raw?.endpoint,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [rowsWithStatusOverrides, statusFilter, tableSearch, universalSearchQuery]);

  const sortedRows = useMemo(
    () =>
      stableSortTableRows(filteredRows, sortConfig, (a, b, key) => {
        if (key === 'severity') {
          return (RISK_SEVERITY_ORDER[a.severity] || 0) - (RISK_SEVERITY_ORDER[b.severity] || 0);
        }
        if (key === 'target') return compareLocaleStrings(a.target, b.target);
        if (key === 'title') return compareLocaleStrings(a.title, b.title);
        if (key === 'scanner') return compareLocaleStrings(a.scannerType || a.source, b.scannerType || b.source);
        if (key === 'cvss') {
          const aScore = Number.parseFloat(a.cvssScore);
          const bScore = Number.parseFloat(b.cvssScore);
          const aValid = Number.isFinite(aScore);
          const bValid = Number.isFinite(bScore);
          if (!aValid && !bValid) return 0;
          if (!aValid) return -1;
          if (!bValid) return 1;
          return aScore - bScore;
        }
        if (key === 'status') return compareLocaleStrings(a.statusLabel || a.status, b.statusLabel || b.status);
        if (key === 'port') return compareLocaleStrings(a.port, b.port);
        return 0;
      }),
    [filteredRows, sortConfig]
  );
  const riskPagination = usePaginatedRows(sortedRows, TABLE_PAGE_SIZE);
  const visibleRiskRows = riskPagination.pageRows;
  tourRiskRowsRef.current = sortedRows;

  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  }, [orgQuery, scannerFilter, severityFilter, statusFilter, dateFilter, rangeFilter, tableSearch, riskPagination.page]);

  // Match header spacer to scrollbar width + the body content inset (gap before scrollbar).
  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [loading, sortedRows.length, riskPagination.page]);

  const visibleRiskIds = useMemo(() => visibleRiskRows.map((row) => row.id), [visibleRiskRows]);
  const allVisibleSelected =
    visibleRiskIds.length > 0 && visibleRiskIds.every((id) => selectedRiskIds.includes(id));
  const someVisibleSelected =
    visibleRiskIds.some((id) => selectedRiskIds.includes(id)) && !allVisibleSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  useEffect(() => {
    setSelectedRiskIds((prev) => {
      const next = prev.filter((id) => visibleRiskIds.includes(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [visibleRiskIds]);

  useEffect(() => {
    if (selectedRiskIds.length === 0) setBulkStatusOpen(false);
  }, [selectedRiskIds.length]);

  const toggleSelectAllVisibleRisks = useCallback(() => {
    setSelectedRiskIds((prev) => {
      if (visibleRiskIds.length === 0) return prev;
      const allSelected = visibleRiskIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !visibleRiskIds.includes(id));
      }
      const merged = new Set(prev);
      visibleRiskIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  }, [visibleRiskIds]);

  const toggleRiskRowSelected = useCallback((rowId) => {
    if (!rowId) return;
    setSelectedRiskIds((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  }, []);

  useEffect(() => {
    const selectFirstRisk = () => {
      const firstId = tourRiskRowsRef.current?.[0]?.id;
      if (!firstId) return;
      setSelectedRiskIds([firstId]);
    };
    const clearRiskSelection = () => {
      setSelectedRiskIds([]);
      setBulkStatusOpen(false);
      setOpenRiskStatusMenuId(null);
    };
    window.addEventListener('xspectre:tour-select-first-risk', selectFirstRisk);
    window.addEventListener('xspectre:tour-clear-risk-selection', clearRiskSelection);
    return () => {
      window.removeEventListener('xspectre:tour-select-first-risk', selectFirstRisk);
      window.removeEventListener('xspectre:tour-clear-risk-selection', clearRiskSelection);
    };
  }, []);

  const handleRiskActionChange = useCallback((rowId, nextStatus) => {
    const normalized = String(nextStatus || '').trim().toLowerCase();
    if (!rowId || !normalized || statusNoteSaving) return;
    const current = String(
      rowsWithStatusOverrides.find((row) => row.id === rowId)?.status || ''
    )
      .trim()
      .toLowerCase();
    if (current === normalized) {
      setOpenRiskStatusMenuId(null);
      return;
    }
    setOpenRiskStatusMenuId(null);
    setBulkStatusOpen(false);
    setStatusNoteError('');
    setStatusNoteValue('');
    setStatusNoteConfirm({
      status: normalized,
      rowIds: [rowId],
      source: 'row',
    });
  }, [rowsWithStatusOverrides, statusNoteSaving]);

  const handleBulkRiskStatusChange = useCallback((nextStatus) => {
    const normalized = String(nextStatus || '').trim().toLowerCase();
    if (!normalized || selectedRiskIds.length === 0 || statusNoteSaving) return;
    setBulkStatusOpen(false);
    setOpenRiskStatusMenuId(null);
    setStatusNoteError('');
    setStatusNoteValue('');
    setStatusNoteConfirm({
      status: normalized,
      rowIds: [...selectedRiskIds],
      source: 'bulk',
    });
  }, [selectedRiskIds, statusNoteSaving]);

  const closeStatusNoteConfirm = useCallback(() => {
    if (statusNoteSaving) return;
    setStatusNoteConfirm(null);
    setStatusNoteValue('');
    setStatusNoteError('');
  }, [statusNoteSaving]);

  const closeDeleteConfirm = useCallback(() => {
    if (deleteBusy) return;
    setDeleteConfirm(null);
    setDeleteError('');
  }, [deleteBusy]);

  const requestBulkDelete = useCallback(() => {
    if (selectedRiskIds.length === 0 || deleteBusy) return;
    setBulkStatusOpen(false);
    setDeleteError('');
    setDeleteConfirm({ rowIds: [...selectedRiskIds] });
  }, [selectedRiskIds, deleteBusy]);

  const confirmBulkDelete = useCallback(async () => {
    const rowIds = Array.isArray(deleteConfirm?.rowIds) ? deleteConfirm.rowIds : [];
    if (rowIds.length === 0 || deleteBusy) return;

    setDeleteBusy(true);
    setDeleteError('');
    const deletedIds = [];
    const failures = [];

    try {
      for (const rowId of rowIds) {
        const row = rows.find((entry) => entry.id === rowId);
        const scanner =
          String(row?.scannerType || row?.raw?.scanner_type || '').trim() || undefined;
        try {
          if (UUID_RE.test(rowId)) {
            await deleteDashboardVulnerability(rowId, { scanner });
          }
          deletedIds.push(rowId);
        } catch (err) {
          failures.push(err?.message || `Failed to delete ${rowId}`);
        }
      }

      if (deletedIds.length > 0) {
        const deletedSet = new Set(deletedIds);
        risksDataRevision.current += 1;
        setRows((prev) => {
          const next = prev.filter((row) => !deletedSet.has(row.id));
          rowsRef.current = next;
          queryClient.setQueryData(risksQueryKeyRef.current, next);
          return next;
        });
        setSelectedRiskIds((prev) => prev.filter((id) => !deletedSet.has(id)));
        setRiskStatusOverrides((prev) => {
          const next = { ...prev };
          deletedIds.forEach((id) => {
            delete next[id];
          });
          return next;
        });
      }

      if (failures.length > 0 && deletedIds.length === 0) {
        setDeleteError(failures[0]);
        return;
      }
      if (failures.length > 0) {
        setDeleteError(`${deletedIds.length} deleted, ${failures.length} failed.`);
        return;
      }
      setDeleteConfirm(null);
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteConfirm, deleteBusy, rows]);

  const applyLocalStatusChange = useCallback((rowIds, normalized) => {
    const idSet = new Set(rowIds);
    risksDataRevision.current += 1;
    setRiskStatusOverrides((prev) => {
      const next = { ...prev };
      rowIds.forEach((id) => {
        next[id] = normalized;
      });
      return next;
    });
    setRows((prev) => {
      const next = prev.map((row) =>
        idSet.has(row.id)
          ? {
              ...row,
              status: normalized,
              statusLabel: riskStatusLabelFromValue(normalized),
            }
          : row
      );
      rowsRef.current = next;
      queryClient.setQueryData(risksQueryKeyRef.current, next);
      return next;
    });
  }, []);

  const confirmStatusNoteChange = useCallback(async () => {
    const noteText = String(statusNoteValue || '').trim();
    if (!statusNoteConfirm || !noteText || statusNoteSaving) return;

    const pendingStatus = statusNoteConfirm.status;
    const rowIds = Array.isArray(statusNoteConfirm.rowIds) ? statusNoteConfirm.rowIds : [];
    if (!pendingStatus || rowIds.length === 0) return;

    const statusLabel = riskStatusLabelFromValue(pendingStatus);
    const noteBody = `Status changed to ${statusLabel}: ${noteText}`;
    const createdAt = new Date().toISOString();

    setStatusNoteSaving(true);
    setStatusNoteError('');

    try {
      for (const rowId of rowIds) {
        const row = rows.find((entry) => entry.id === rowId);
        const scanner =
          String(row?.scannerType || row?.raw?.scanner_type || '').trim() || undefined;

        if (UUID_RE.test(rowId)) {
          await postDashboardVulnerabilityNote(rowId, { note: noteBody, scanner });
          await patchDashboardVulnerabilityStatus(rowId, { status: pendingStatus, scanner });
        } else {
          const nextNotes = [
            { id: `note-${createdAt}-${rowId}`, user: 'You', createdAt, text: noteBody },
            ...loadLocalRiskNotes(rowId),
          ];
          persistLocalRiskNotes(rowId, nextNotes);
        }
      }

      applyLocalStatusChange(rowIds, pendingStatus);
      if (statusNoteConfirm.source === 'bulk') {
        setSelectedRiskIds([]);
      }
      setStatusNoteConfirm(null);
      setStatusNoteValue('');
    } catch (err) {
      setStatusNoteError(err?.message || 'Unable to save status note');
    } finally {
      setStatusNoteSaving(false);
    }
  }, [
    applyLocalStatusChange,
    rows,
    statusNoteConfirm,
    statusNoteSaving,
    statusNoteValue,
  ]);

  useEffect(() => {
    if (!statusNoteConfirm) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeStatusNoteConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [statusNoteConfirm, closeStatusNoteConfirm]);

  useEffect(() => {
    if (!deleteConfirm) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeDeleteConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteConfirm, closeDeleteConfirm]);

  const openRiskDetail = useCallback((row) => {
    const id = String(row?.id || '').trim();
    if (!id) return;
    navigate(riskDetailPath(id, row?.scannerType), { state: { riskRow: row } });
  }, [navigate]);

  useEffect(() => {
    const openFirstRisk = () => {
      if (document.querySelector('[data-tour="risk-detail-page"]')) return;
      const row = tourRiskRowsRef.current?.[0];
      if (!row?.id) return;
      openRiskDetail(row);
    };
    window.addEventListener('xspectre:tour-open-first-risk', openFirstRisk);
    return () => {
      window.removeEventListener('xspectre:tour-open-first-risk', openFirstRisk);
    };
  }, [openRiskDetail]);

  const stopRiskRowAction = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleRiskRowKeyDown = useCallback((row, event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openRiskDetail(row);
    }
  }, [openRiskDetail]);

  const selectedRiskRows = useMemo(
    () => sortedRows.filter((row) => selectedRiskIds.includes(row.id)),
    [selectedRiskIds, sortedRows]
  );

  const selectedAssets = useMemo(() => {
    const seen = new Set();
    return selectedRiskRows
      .map((row) => ({
        id: row.target || row.id,
        name: row.target || row.id,
        scanner_type: row.scannerType,
      }))
      .filter((item) => {
        const key = `${item.id}:${item.scanner_type}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [selectedRiskRows]);

  const selectedDataSourceKeys = useMemo(() => {
    const keys = new Set();
    selectedRiskRows.forEach((row) => {
      inferSourceKeysFromRiskRow(row).forEach((key) => keys.add(String(key).trim().toLowerCase()));
    });
    return Array.from(keys);
  }, [selectedRiskRows]);

  const selectedReportTemplate = useMemo(
    () => REPORT_TYPE_OPTIONS.find((item) => String(item.template_key) === String(reportTemplateKey)) || null,
    [reportTemplateKey]
  );

  const selectedRiskRowsInDateRange = useMemo(() => {
    if (!reportDateFrom && !reportDateTo) return selectedRiskRows;

    const fromTs = reportDateFrom ? new Date(reportDateFrom).getTime() : null;
    const toTsRaw = reportDateTo ? new Date(reportDateTo).getTime() : null;
    const toTs = toTsRaw === null ? null : (toTsRaw + (24 * 60 * 60 * 1000) - 1);

    return selectedRiskRows.filter((row) => {
      const detectedTs = row?.detectedAt ? new Date(row.detectedAt).getTime() : null;
      if (!Number.isFinite(detectedTs)) return false;
      if (fromTs !== null && detectedTs < fromTs) return false;
      if (toTs !== null && detectedTs > toTs) return false;
      return true;
    });
  }, [reportDateFrom, reportDateTo, selectedRiskRows]);

  const selectedAssetsInDateRange = useMemo(() => {
    const seen = new Set();
    return selectedRiskRowsInDateRange
      .map((row) => ({
        id: row.target || row.id,
        name: row.target || row.id,
        scanner_type: row.scannerType,
      }))
      .filter((item) => {
        const key = `${item.id}:${item.scanner_type}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [selectedRiskRowsInDateRange]);

  const handleGenerateReportFromSelection = useCallback(async () => {
    if (selectedRiskRowsInDateRange.length === 0 || !selectedReportTemplate) return;
    setReportGenerationBusy(true);
    setReportGenerationError('');
    try {
      const nowIso = new Date().toISOString();
      const findings = selectedRiskRowsInDateRange.map((row) => ({
        finding_id: row.id,
        title: row.title,
        severity: row.severity,
        scanner_type: row.scannerType,
        target: row.target,
        port: row.port,
        cvss: row.cvssScore,
        status: row.status,
        detected_at: row.detectedAt,
      }));

      const findingIds = findings
        .map((item) => String(item.finding_id || '').trim())
        .filter(Boolean);
      const assetIds = selectedAssetsInDateRange
        .map((item) => String(item.id || '').trim())
        .filter(Boolean);
      const snapshotReference = `vuln-selection:${Date.now()}:${findingIds.length}`;

      await generateAIReport(
        {
          source_scope: 'selected_findings_assets',
          generated_at: nowIso,
          finding_count: findings.length,
          asset_count: selectedAssetsInDateRange.length,
          source_keys: selectedDataSourceKeys,
          source_integration_keys: selectedDataSourceKeys,
          evidence_snapshot: {
            snapshot_reference: snapshotReference,
            captured_at: nowIso,
            finding_ids: findingIds,
            asset_ids: assetIds,
            source_keys: selectedDataSourceKeys,
          },
          finding_ids: findingIds,
          asset_ids: assetIds,
          findings,
          assets: selectedAssetsInDateRange,
        },
        {
          scope: 'selected_findings_assets',
          reportType: selectedReportTemplate.template_key,
          templateKey: selectedReportTemplate.template_key,
          findingIds,
          assetIds,
          aiEnhancement: true,
        }
      );

      setReportModalOpen(false);
      setSelectedRiskIds([]);
      setReportDateFrom('');
      setReportDateTo('');
      navigate('/dashboard/reports');
    } catch (err) {
      setReportGenerationError(err?.message || 'Failed to generate report from selected findings/assets.');
    } finally {
      setReportGenerationBusy(false);
    }
  }, [navigate, selectedAssetsInDateRange, selectedDataSourceKeys, selectedReportTemplate, selectedRiskRowsInDateRange]);

  if (loading) {
    return (
      <div className="risks-page-stack" data-tour="risks-page">
        <PageDataLoader label="Loading vulnerabilities" />
      </div>
    );
  }

  return (
    <div className="risks-page-stack">
      {isRiskFilterPanelOpen ? (
        <div
          className="risks-filter-dismiss-backdrop"
          role="presentation"
          aria-hidden="true"
          onMouseDown={closeRiskFilterPanels}
        />
      ) : null}
      <div className="risks-page-filters" role="region" aria-label="Vulnerability filters">
        {selectedCount > 0 ? (
          <div className="risks-bulk-bar" role="status" aria-live="polite" data-tour="risks-bulk-actions">
            <div className="risks-bulk-status" ref={bulkStatusRef}>
              <button
                type="button"
                className={`risks-bulk-status-btn${bulkStatusOpen ? ' is-open' : ''}`}
                aria-expanded={bulkStatusOpen}
                aria-haspopup="listbox"
                aria-label={`Change status for ${selectedCount} selected vulnerabilit${selectedCount === 1 ? 'y' : 'ies'}`}
                onClick={() => {
                  setSeverityOpen(false);
                  setScannerOpen(false);
                  setStatusOpen(false);
                  setBulkStatusOpen((open) => !open);
                }}
              >
                <span>Change status ({selectedCount})</span>
                <span className="risks-bulk-status-chevron" aria-hidden="true" />
              </button>
              {bulkStatusOpen ? (
                <ul className="risks-bulk-status-menu" role="listbox" aria-label="Bulk status options">
                  {RISK_STATUS_ACTION_OPTIONS.map((opt) => (
                    <li key={`bulk-status-${opt.value}`} role="presentation">
                      <button
                        type="button"
                        role="option"
                        className={`risks-bulk-status-option risks-bulk-status-option--${opt.value}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleBulkRiskStatusChange(opt.value)}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <button
              type="button"
              className="risks-bulk-delete-btn"
              onClick={requestBulkDelete}
              disabled={deleteBusy || selectedCount === 0}
              aria-label={`Delete ${selectedCount} selected vulnerabilit${selectedCount === 1 ? 'y' : 'ies'}`}
            >
              {`Delete (${selectedCount})`}
            </button>
          </div>
        ) : null}
        <label className="risks-page-filter-field" data-tour="risks-severity-filter">
          <div className="users-orgs-users-company-filter" ref={severityRef}>
            <div className="users-orgs-users-company-filter-bar">
              <button
                type="button"
                className={`users-orgs-users-company-filter-main${
                  !severityFilter ? ' users-orgs-users-company-filter-main--all-orgs' : ''
                }`}
                aria-expanded={severityOpen}
                aria-haspopup="listbox"
                onClick={() => {
                  setScannerOpen(false);
                  setStatusOpen(false);
                  setBulkStatusOpen(false);
                  setSeverityOpen((open) => !open);
                }}
              >
                <span className="users-orgs-users-company-filter-label">{selectedSeverityLabel}</span>
                <span className="users-orgs-users-company-filter-chevron" aria-hidden="true"></span>
              </button>
              {severityFilter ? (
                <button
                  type="button"
                  className="users-orgs-users-company-filter-clear"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSeverityFilter('');
                    setSeverityOpen(false);
                  }}
                  aria-label="Clear threat filter"
                  title="Clear threat filter"
                >
                  <svg
                    width={14}
                    height={14}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              ) : null}
            </div>
            {severityOpen ? (
              <div className="users-orgs-users-company-filter-panel" role="presentation">
                <ul className="users-orgs-users-company-filter-list" role="listbox">
                  {severityOptions.map((opt) => (
                    <li key={`risk-severity-${opt.value || 'all'}`} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={severityFilter === opt.value}
                        className={`users-orgs-users-company-filter-option${severityFilter === opt.value ? ' is-selected' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSeverityFilter(opt.value);
                          setSeverityOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </label>
        <label className="risks-page-filter-field" data-tour="risks-scanner-filter">
          <div className="users-orgs-users-company-filter" ref={scannerRef}>
            <div className="users-orgs-users-company-filter-bar">
              <button
                type="button"
                className={`users-orgs-users-company-filter-main${
                  !scannerFilter ? ' users-orgs-users-company-filter-main--all-orgs' : ''
                }`}
                aria-expanded={scannerOpen}
                aria-haspopup="listbox"
                onClick={() => {
                  setSeverityOpen(false);
                  setStatusOpen(false);
                  setBulkStatusOpen(false);
                  setScannerOpen((open) => !open);
                }}
              >
                <span className="users-orgs-users-company-filter-label">{selectedScannerLabel}</span>
                <span className="users-orgs-users-company-filter-chevron" aria-hidden="true"></span>
              </button>
              {scannerFilter ? (
                <button
                  type="button"
                  className="users-orgs-users-company-filter-clear"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setScannerFilter('');
                    setScannerOpen(false);
                  }}
                  aria-label="Clear scanner type filter"
                  title="Clear scanner type filter"
                >
                  <svg
                    width={14}
                    height={14}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              ) : null}
            </div>
            {scannerOpen ? (
              <div className="users-orgs-users-company-filter-panel" role="presentation">
                <ul className="users-orgs-users-company-filter-list" role="listbox">
                  {scannerOptions.map((opt) => (
                    <li key={`risk-scanner-${opt.value || 'all'}`} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={scannerFilter === opt.value}
                        className={`users-orgs-users-company-filter-option${scannerFilter === opt.value ? ' is-selected' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setScannerFilter(opt.value);
                          setScannerOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </label>
        <label className="risks-page-filter-field" data-tour="risks-status-filter">
          <div className="users-orgs-users-company-filter" ref={statusRef}>
            <div className="users-orgs-users-company-filter-bar">
              <button
                type="button"
                className={`users-orgs-users-company-filter-main${
                  !statusFilter ? ' users-orgs-users-company-filter-main--all-orgs' : ''
                }`}
                aria-expanded={statusOpen}
                aria-haspopup="listbox"
                onClick={() => {
                  setSeverityOpen(false);
                  setScannerOpen(false);
                  setBulkStatusOpen(false);
                  setStatusOpen((open) => !open);
                }}
              >
                <span className="users-orgs-users-company-filter-label">{selectedStatusLabel}</span>
                <span className="users-orgs-users-company-filter-chevron" aria-hidden="true"></span>
              </button>
              {statusFilter ? (
                <button
                  type="button"
                  className="users-orgs-users-company-filter-clear"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setStatusFilter('');
                    setStatusOpen(false);
                  }}
                  aria-label="Clear status filter"
                  title="Clear status filter"
                >
                  <svg
                    width={14}
                    height={14}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              ) : null}
            </div>
            {statusOpen ? (
              <div className="users-orgs-users-company-filter-panel" role="presentation">
                <ul className="users-orgs-users-company-filter-list" role="listbox">
                  {statusOptions.map((opt) => (
                    <li key={`risk-status-${opt.value}`} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={statusFilter === opt.value}
                        className={`users-orgs-users-company-filter-option${statusFilter === opt.value ? ' is-selected' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setStatusFilter(opt.value);
                          setStatusOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </label>
        {dateFilter || rangeFilter ? (
          <div
            className={`risks-page-date-filter-chip${dateFilterRelaxed ? ' is-relaxed' : ''}`}
            data-tour="risks-date-filter"
            title={
              dateFilterRelaxed
                ? dateFilter
                  ? 'No findings matched this detection date. Showing severity results without the day filter.'
                  : 'Detection dates were missing on some rows. Showing API range results as returned.'
                : undefined
            }
          >
            <span className="risks-page-date-filter-chip-label">
              {dateFilterRelaxed ? 'Date (approx):' : dateFilter ? 'Date:' : 'Range:'}{' '}
              {dateFilter ? formatRiskDateFilterLabel(dateFilter) : formatRiskRangeFilterLabel(rangeFilter)}
            </span>
            <button
              type="button"
              className="risks-page-date-filter-chip-clear"
              aria-label={dateFilter ? 'Clear date filter' : 'Clear range filter'}
              title={dateFilter ? 'Clear date filter' : 'Clear range filter'}
              onClick={() => {
                setDateFilter('');
                setRangeFilter('');
                setDateFilterRelaxed(false);
                navigate(
                  buildRisksPathWithFilters({
                    severity: severityFilter || undefined,
                    scanner: scannerFilter || undefined,
                    status: statusFilter || 'all',
                    q: tableSearch || undefined,
                  })
                );
              }}
            >
              ✕
            </button>
          </div>
        ) : null}
        <div className="subscription-org-management-search risks-page-table-search" data-tour="risks-search">
          <label htmlFor={tableSearchId} className="edi-sr-only">
            Search vulnerabilities
          </label>
          <div className="subscription-org-management-search-inner">
            <input
              id={tableSearchId}
              type="search"
              className="subscription-org-management-search-input"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search vulnerabilities"
              autoComplete="off"
              spellCheck={false}
            />
            {tableSearch ? (
              <button
                type="button"
                className="ghost-exposure-sys-table-search-clear"
                onClick={() => setTableSearch('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {headerActionsHost && selectedRiskIds.length > 0
        ? createPortal(
            <button
              type="button"
              className="new-scan-btn risks-generate-report-btn page-header-cta"
              data-tour="risks-generate-report"
              onClick={() => {
                setReportGenerationError('');
                setReportDateFrom('');
                setReportDateTo('');
                setReportModalOpen(true);
              }}
            >
              <svg
                className="risks-generate-report-btn-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M8 13h8" />
                <path d="M8 17h5" />
              </svg>
              Generate Report ({selectedRiskIds.length})
            </button>,
            headerActionsHost
          )
        : null}

      {error ? <div className="profile-settings-message profile-settings-error" role="alert">{error}</div> : null}
      {reportGenerationError ? (
        <div className="profile-settings-message profile-settings-error" role="alert">{reportGenerationError}</div>
      ) : null}

      {reportModalOpen ? (
        <div className="risks-report-modal-backdrop" role="presentation" onClick={() => setReportModalOpen(false)}>
          <div className="risks-report-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={() => setReportModalOpen(false)}
              aria-label="Close"
              disabled={reportGenerationBusy}
            >
              ✕
            </button>
            <h3>Generate Report from Selection</h3>
            <p>
              Selected Findings: <strong>{selectedRiskRows.length}</strong> | Selected Assets: <strong>{selectedAssets.length}</strong>
            </p>
            <label className="risks-report-modal-label" htmlFor="risks-report-template">
              Report Type
            </label>
            <select
              id="risks-report-template"
              className="profile-settings-input"
              value={reportTemplateKey}
              onChange={(e) => setReportTemplateKey(e.target.value)}
            >
              {REPORT_TYPE_OPTIONS.map((template) => (
                <option key={template.template_key} value={template.template_key}>{template.name}</option>
              ))}
            </select>
            <label className="risks-report-modal-label" htmlFor="risks-report-date-from">Date Range From</label>
            <input
              id="risks-report-date-from"
              type="date"
              className="profile-settings-input"
              value={reportDateFrom}
              onChange={(e) => setReportDateFrom(e.target.value)}
            />
            <label className="risks-report-modal-label" htmlFor="risks-report-date-to">Date Range To</label>
            <input
              id="risks-report-date-to"
              type="date"
              className="profile-settings-input"
              value={reportDateTo}
              onChange={(e) => setReportDateTo(e.target.value)}
            />
            <p className="risks-report-modal-empty">
              Findings in selected range: <strong>{selectedRiskRowsInDateRange.length}</strong> | Assets in selected range: <strong>{selectedAssetsInDateRange.length}</strong>
            </p>
            <div className="risks-report-modal-actions">
              <button type="button" className="new-scan-btn" onClick={() => setReportModalOpen(false)} disabled={reportGenerationBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="new-scan-btn"
                onClick={handleGenerateReportFromSelection}
                disabled={reportGenerationBusy || !selectedReportTemplate || selectedRiskRowsInDateRange.length === 0}
                title={selectedRiskRowsInDateRange.length === 0 ? 'No findings in selected date range.' : ''}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="risks-page" data-tour="risks-page">
      <div
        className="assets-table-shell risks-page-table-shell assets-table-shell--inventory-body"
        data-tour="risks-table"
      >
        {loading ? (
          <div className="risks-page-table-loading">
            <BlueXLoader />
          </div>
        ) : (
          <>
            <div
              className="risks-page-table-header-frame"
              style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
            >
              <div ref={tableHeaderRef} className="risks-page-table-header">
                <table
                className="assets-table risks-page-table risks-page-table--resizable risks-page-table--header"
                style={{ width: '100%', minWidth: risksTableMinWidth }}
                >
                <colgroup>
                  {RISKS_TABLE_COLUMNS.map((col) => {
                    const isFixedWidth = col.key === 'checkbox' || col.key === 'status';
                    const px = Math.round(Number(riskColumnWidths[col.key]) || col.defaultWidth);
                    return (
                      <col
                        key={col.key}
                        data-col-key={col.key}
                        style={{
                          width: `${px}px`,
                          minWidth: `${px}px`,
                          ...(isFixedWidth ? { maxWidth: `${px}px` } : null),
                        }}
                      />
                    );
                  })}
                </colgroup>
                <thead>
                  <tr>
                    {RISKS_TABLE_COLUMNS.map((col, index) => (
                      <th
                        key={col.key}
                        scope="col"
                        className={
                          col.key === 'checkbox'
                            ? 'risks-page-th-checkbox'
                            : col.key === 'port'
                              ? 'risks-page-th-port'
                              : undefined
                        }
                        aria-sort={
                          col.sortKey && sortConfig.key === col.sortKey
                            ? sortConfig.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : col.sortKey
                              ? 'none'
                              : undefined
                        }
                      >
                        <div className="risks-page-th-inner">
                          {col.key === 'checkbox' ? (
                            <span data-tour="risks-select-all">
                              <input
                                ref={selectAllRef}
                                type="checkbox"
                                className="risks-page-checkbox"
                                checked={allVisibleSelected}
                                disabled={sortedRows.length === 0}
                                onChange={toggleSelectAllVisibleRisks}
                                aria-label="Select all vulnerabilities in this list"
                              />
                            </span>
                          ) : col.sortKey ? (
                            <button
                              type="button"
                              className="assets-sort-btn risks-page-sort-btn"
                              onClick={() => handleRiskSort(col.sortKey)}
                            >
                              {col.label}{' '}
                              <span
                                className={`assets-sort-icon ${sortConfig.key === col.sortKey ? 'is-active' : ''}`}
                                aria-hidden="true"
                              >
                                {getRiskSortIcon(col.sortKey)}
                              </span>
                            </button>
                          ) : (
                            <span className="risks-page-th-label">{col.label}</span>
                          )}
                          {col.resizable !== false && index < RISKS_TABLE_COLUMNS.length - 1 ? (
                            <span
                              className={`risks-page-col-resize-handle${riskColumnResizingKey === col.key ? ' is-active' : ''}`}
                              role="separator"
                              aria-orientation="vertical"
                              aria-label={`Resize ${col.label} column`}
                              onMouseDown={(e) => onRiskColumnResizeStart(col.key, e)}
                            />
                          ) : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                </table>
              </div>
              <div className="risks-page-table-header-gutter" aria-hidden="true" />
            </div>
            <div
              ref={tableScrollRef}
              className="assets-inventory-table-scroll risks-page-table-scroll"
              onScroll={handleTableScroll}
            >
              <table
                className="assets-table risks-page-table risks-page-table--resizable risks-page-table--body"
                style={{ width: '100%', minWidth: risksTableMinWidth }}
              >
                <colgroup>
                  {RISKS_TABLE_COLUMNS.map((col) => {
                    const isFixedWidth = col.key === 'checkbox' || col.key === 'status';
                    const px = Math.round(Number(riskColumnWidths[col.key]) || col.defaultWidth);
                    return (
                      <col
                        key={col.key}
                        data-col-key={col.key}
                        style={{
                          width: `${px}px`,
                          minWidth: `${px}px`,
                          ...(isFixedWidth ? { maxWidth: `${px}px` } : null),
                        }}
                      />
                    );
                  })}
                </colgroup>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr className="risks-page-empty-row">
                      <td colSpan={RISKS_TABLE_COLUMNS.length} className="assets-empty-cell risks-page-empty-cell">
                        No vulnerabilities found for the current filters.
                      </td>
                    </tr>
                  ) : (
                    visibleRiskRows.map((row, rowIndex) => {
                      const isSelected = selectedRiskIds.includes(row.id);
                      const isTourPrimaryRow = rowIndex === 0;
                      return (
                        <tr
                          key={row.id}
                          className={`risks-page-table-row risks-page-table-row--clickable${isSelected ? ' is-selected' : ''}`}
                          tabIndex={0}
                          data-tour={isTourPrimaryRow ? 'risks-row' : undefined}
                          aria-label={`View vulnerability details for ${row.title || row.target || 'this finding'}`}
                          onClick={() => openRiskDetail(row)}
                          onKeyDown={(event) => handleRiskRowKeyDown(row, event)}
                        >
                          <td
                            className="risks-page-td-checkbox"
                            onClick={stopRiskRowAction}
                            onMouseDown={stopRiskRowAction}
                            onKeyDown={stopRiskRowAction}
                          >
                            <input
                              type="checkbox"
                              className="risks-page-checkbox"
                              checked={isSelected}
                              onChange={() => toggleRiskRowSelected(row.id)}
                              aria-label={`Select vulnerability ${row.title || row.target || row.id}`}
                            />
                          </td>
                          <td>
                            <div className="risks-list-threat">
                              <span
                                className={`risks-list-threat-bars risks-list-threat-bars--${row.severityKey}`}
                                aria-hidden="true"
                              >
                                <span />
                                <span />
                                <span />
                                <span />
                              </span>
                              <span className={`risks-list-threat-label risks-list-threat-label--${row.severityKey}`}>
                                {row.severity}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="risks-list-target">{row.target}</span>
                          </td>
                          <td className="risks-page-td-port">
                            {row.port ? (
                              <span className="risks-list-port-chip">{row.port}</span>
                            ) : (
                              <span className="risks-list-port-empty">—</span>
                            )}
                          </td>
                          <td>
                            <span className={`risks-list-cvss risks-list-cvss--${getCvssSeverityBand(row.cvssScore)}`}>
                              {formatRiskCvssScore(row.cvssScore)}
                            </span>
                          </td>
                          <td>
                            <div className="risks-list-vulnerability">
                              <div className="risks-list-title">{row.title}</div>
                            </div>
                          </td>
                          <td>
                            <div className="risks-list-scanner-source">
                              {formatRiskScannerLabel(row.scannerType, row.scannerDeployment || row.raw?.scanner_deployment)}
                            </div>
                          </td>
                          <td
                            onClick={stopRiskRowAction}
                            onMouseDown={stopRiskRowAction}
                            data-tour={isTourPrimaryRow ? 'risks-row-status' : undefined}
                          >
                            <div className="risks-list-status-actions-cell">
                              <label className="edi-sr-only" htmlFor={`risk-action-${row.id}`}>
                                Status for {row.title}
                              </label>
                              <RiskStatusSelect
                                rowId={row.id}
                                value={row.status}
                                label={row.statusLabel || riskStatusLabelFromValue(row.status)}
                                ariaLabel={`Status for ${row.title || row.target || row.id}`}
                                open={openRiskStatusMenuId === row.id}
                                onOpenChange={(nextOpen) => {
                                  setOpenRiskStatusMenuId(nextOpen ? row.id : null);
                                }}
                                onChange={(nextStatus) => handleRiskActionChange(row.id, nextStatus)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      </div>
      <div className="risks-page-pagination" data-tour="risks-pagination">
        <TablePagination {...riskPagination} itemLabel="risks" />
      </div>

      {statusNoteConfirm ? (
        <div
          className="risks-status-note-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !statusNoteSaving) {
              closeStatusNoteConfirm();
            }
          }}
        >
          <div
            className="risks-status-note-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Add note for status change"
            aria-describedby={statusNoteDescId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeStatusNoteConfirm}
              disabled={statusNoteSaving}
              aria-label="Close"
            >
              ✕
            </button>
            <p id={statusNoteDescId} className="assets-exclude-confirm-desc">
              Add a note to change status to{' '}
              <strong>{riskStatusLabelFromValue(statusNoteConfirm.status)}</strong>
              {statusNoteConfirm.rowIds.length > 1
                ? ` for ${statusNoteConfirm.rowIds.length} selected vulnerabilities.`
                : '.'}
              <span className="assets-exclude-confirm-warning">
                A note is required when changing risk status.
              </span>
            </p>
            <label htmlFor={statusNoteInputId} className="edi-sr-only">
              Status change note
            </label>
            <textarea
              id={statusNoteInputId}
              className="risks-status-note-input"
              value={statusNoteValue}
              onChange={(event) => setStatusNoteValue(event.target.value)}
              placeholder="Explain why the status is changing…"
              rows={4}
              disabled={statusNoteSaving}
              autoFocus
            />
            {statusNoteError ? (
              <div className="risks-status-note-error" role="alert">
                {statusNoteError}
              </div>
            ) : null}
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn"
                onClick={closeStatusNoteConfirm}
                disabled={statusNoteSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="assets-exclude-confirm-btn risks-status-note-confirm-btn"
                onClick={confirmStatusNoteChange}
                disabled={statusNoteSaving || !String(statusNoteValue || '').trim()}
              >
                Save & update status
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDeleteConfirm();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete vulnerabilities"
            aria-describedby={deleteConfirmDescId}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeDeleteConfirm}
              aria-label="Close"
              disabled={deleteBusy}
            >
              ✕
            </button>
            <p id={deleteConfirmDescId} className="assets-exclude-confirm-desc">
              You are about to permanently delete{' '}
              <strong>
                {deleteConfirm.rowIds.length} vulnerabilit
                {deleteConfirm.rowIds.length === 1 ? 'y' : 'ies'}
              </strong>
              .
              <span className="assets-exclude-confirm-warning">
                This action cannot be undone.
              </span>
            </p>
            {deleteError ? (
              <div className="risks-status-note-error" role="alert">
                {deleteError}
              </div>
            ) : null}
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmBulkDelete}
                disabled={deleteBusy}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function RiskFindingLabelIcon({ kind }) {
  const common = {
    viewBox: '0 0 16 16',
    width: 14,
    height: 14,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    className: 'risk-card-label-icon',
  };

  if (kind === 'critical') {
    return (
      <svg {...common}>
        <path d="M8 1.8 14.2 13H1.8L8 1.8z" />
        <path d="M8 6.2v3.2M8 11.4h.01" />
      </svg>
    );
  }
  if (kind === 'high') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6" />
        <path d="M8 4.8v4M8 11.2h.01" />
      </svg>
    );
  }
  if (kind === 'medium') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6" />
        <path d="M5.2 8h5.6" />
      </svg>
    );
  }
  if (kind === 'low') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6" />
        <path d="M8 4.6v4.2l2.2 1.4" />
      </svg>
    );
  }
  if (kind === 'accepted') {
    /* Flag — accepted / acknowledged risk (no tick) */
    return (
      <svg {...common}>
        <path d="M4.2 13.2V2.6" />
        <path d="M4.2 3h7l-1.5 2.5L11.2 8H4.2" />
      </svg>
    );
  }
  /* Resolved — check in circle */
  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="6" />
      <path d="M5.2 8.1 7.2 10.1 11 5.8" />
    </svg>
  );
}

function RiskFindingLabel({ kind, children }) {
  return (
    <span className="risk-card-label">
      <RiskFindingLabelIcon kind={kind} />
      <span className="risk-card-label-text">{children}</span>
    </span>
  );
}

const RisksDetected = ({ summary }) => {
  const crit = summary?.critical_count_site ?? summary?.critical ?? 0;
  const high = summary?.high_count_site ?? summary?.high ?? 0;
  const med = summary?.medium_count_site ?? summary?.medium ?? 0;
  const low = summary?.low_count_site ?? summary?.low ?? 0;
  const accepted = summary?.accepted_count_site ?? summary?.total_accepted ?? 0;
  const closed = summary?.close_count_site ?? summary?.total_closed ?? 0;

  return (
    <div className="risks-container risks-container--overview" data-tour="open-security-findings">
      <div className="posture-dual__column-body risks-findings-body">
        <div className="risks-findings-header risks-overview-header">
          <h2 className="dashboard-posture-heading risks-title">
            Open Security Findings
            <InfoTooltip label="About Open Security Findings" title="Open Security Findings">
              <p>Open findings by severity, plus accepted and resolved counts for this scope.</p>
            </InfoTooltip>
          </h2>
          <Link
            className="risks-findings-view-all risks-view-all"
            to={buildRisksPathWithFilters({ status: 'open' })}
          >
            View all
            <span aria-hidden="true">{' \u2192'}</span>
          </Link>
        </div>
        <div className="risks-grid risks-grid--findings">
          <Link
            to={buildRisksPathWithFilters({ severity: 'Critical', status: 'open' })}
            className="risk-card critical dashboard-card-link"
          >
            <RiskFindingLabel kind="critical">Critical</RiskFindingLabel>
            <span className="risk-card-value">{crit}</span>
          </Link>
          <Link
            to={buildRisksPathWithFilters({ severity: 'High', status: 'open' })}
            className="risk-card high dashboard-card-link"
          >
            <RiskFindingLabel kind="high">High</RiskFindingLabel>
            <span className="risk-card-value">{high}</span>
          </Link>
          <Link
            to={buildRisksPathWithFilters({ severity: 'Medium', status: 'open' })}
            className="risk-card medium dashboard-card-link"
          >
            <RiskFindingLabel kind="medium">Medium</RiskFindingLabel>
            <span className="risk-card-value">{med}</span>
          </Link>
          <Link
            to={buildRisksPathWithFilters({ severity: 'Low', status: 'open' })}
            className="risk-card low dashboard-card-link"
          >
            <RiskFindingLabel kind="low">Low</RiskFindingLabel>
            <span className="risk-card-value">{low}</span>
          </Link>
          <Link
            to={buildRisksPathWithFilters({ status: 'accepted' })}
            className="risk-card accepted dashboard-card-link"
          >
            <RiskFindingLabel kind="accepted">Accepted</RiskFindingLabel>
            <span className="risk-card-value">{accepted}</span>
          </Link>
          <Link
            to={buildRisksPathWithFilters({ status: 'closed' })}
            className="risk-card resolved dashboard-card-link"
          >
            <RiskFindingLabel kind="resolved">Resolved</RiskFindingLabel>
            <span className="risk-card-value">{closed}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RisksPageContent;
export {
  RisksDetected,
  normalizeRiskRow,
  normalizeRiskSeverity,
  riskStatusLabelFromValue,
  resolveRiskDetailStatusValue,
  scanVulnToRiskRow,
  RISK_SEVERITY_ORDER,
};
